import Button from '@components/ui/button'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import saveListService from '@services/savelistservice'
import { useMutation } from 'react-query'
import { getProductsBySkus } from '@framework/product/get-product'
import { Item } from '@contexts/cart/cart.utils'
import { useCart } from '@contexts/cart/cart.context'
import ErrorMessage from './quick-order-error'
import UploadIcon from '@components/icons/quickorder/upload-icon'
import Link from '@components/ui/link'
import Papa from 'papaparse'

const CreateImportOrder: FC = () => {
  const { t: translate } = useTranslation(['savelist', 'quickorder', 'cart'])
  const [errorSkus, setErrorSkus] = useState<string | null>(null)
  const [isOrderLoading, setIsOrderLoading] = useState(false)
  const [onlyError, setOnlyError] = useState(false)
  const { locale } = useRouter()
  const { addOrUpdateItemsHandler } = useCart()
  const [selectedFile, setSelectedFile] = useState<any>()
  const [sameplCsv, setSameplCsv] = useState('')
  const [items, setItems] = useState<{ sku: string; qty: number }[]>([])

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setSelectedFile(file)
    if (file) {
      Papa.parse(file, {
        header: true, // Automatically maps CSV headers to keys
        skipEmptyLines: true,
        complete: (result) => {
          const formattedData = result.data.map((row: any) => ({
            sku: row.sku,
            qty: parseInt(row.qty, 10),
          }))
          setItems(formattedData)
        },
        error: (error) => {
          console.error('Error parsing CSV:', error)
        },
      })
    }
  }
  const getTypesenseProducts = async () => {
    const skusString = items.map((item: { sku: string }) => item.sku).join(',')
    let quickEntryListItems: any = items
    let selectedListTypesenseItems = await getProductsBySkus(
      skusString,
      quickEntryListItems,
      locale,
    )
    return selectedListTypesenseItems
  }

  const getdownloadSampleCsv = async () => {
    try {
      const response: any = await saveListService.downloadSampleCsv()
      setSameplCsv(response)
    } catch (err) {
      console.error('Failed to fetch products', err)
    }
  }

  useEffect(() => {
    getdownloadSampleCsv()
  }, [])

  const { mutate: createOrder, isLoading } = useMutation(
    ({
      payload,
      selectedListItems,
    }: {
      payload: FormData
      selectedListItems: any[]
    }) => {
      return saveListService.createOrderByImportCsv(payload)
    },
    {
      onSuccess: async (
        response: any,
        { selectedListItems }: { selectedListItems: Item[] },
      ) => {
        setSelectedFile(null)
        setItems
        if (response?.errorSkus) setErrorSkus(response?.errorSkus)
        if (response?.status) {
          toast.success(response?.message)
          setOnlyError(false)
        } else {
          toast.error(response?.message, {
            position: toast.POSITION.TOP_RIGHT,
            className: 'toast-error',
          })
          setOnlyError(true)
          return
        }
        if (response?.errorSkusList?.length) {
          let updatedSelectedListItems = await selectedListItems.filter(
            (selectedItem) =>
              response.errorSkusList.some(
                (errorItem) => errorItem.sku !== selectedItem.sku,
              ),
          )
          updatedSelectedListItems = updatedSelectedListItems.length
            ? updatedSelectedListItems
            : selectedListItems
          addOrUpdateItemsHandler(updatedSelectedListItems)
        } else {
          addOrUpdateItemsHandler(selectedListItems)
        }
      },
      onError: (err: Error) => {
        toast.error(`Error list updation: ${err?.message}`, {
          position: toast.POSITION.TOP_RIGHT,
          className: 'toast-error',
        })
      },
    },
  )

  const handleUpload = async () => {
    if (selectedFile && items.length) {
      const formData = new FormData()
      // Append the file to the FormData object with the key 'quick_order_csv'
      formData.append('quick_order_csv', selectedFile)
      setErrorSkus(null)
      setIsOrderLoading(true)
      try {
        const items = await getTypesenseProducts()
        setIsOrderLoading(false)
        createOrder({
          payload: formData,
          selectedListItems: items, // Ensure this matches the expected type
        })
      } catch (err) {
        console.error(
          'Error fetching typesense products or creating order:',
          err,
        )
        toast.error(err)
        setIsOrderLoading(false) // Ensure loading state is reset on error
      }
    } else
      toast.error(
        translate('text-select-file-to-upload', { ns: 'quickorder' }),
        {
          className: 'toast-error',
        },
      )
  }

  return (
    <>
      {errorSkus ? (
        <ErrorMessage reportLink={errorSkus} onlyError={onlyError} />
      ) : null}

      <div className="q-order-import">
        <div className="w-4/5 mx-auto mt-20 mb-12 flex flex-col">
          <p className="text-[16px] underline text-brand font-semibold mb-[5px]">
            <Link
              target="_blank"
              href={sameplCsv}
              className="text-brand underline"
            >
              {translate('text-download-sample-csv', { ns: 'quickorder' })}
            </Link>
          </p>
          <div className="border border-dashed border-[#939393] mb-[50px] h-[200px] flex justify-center items-center">
            <label
              htmlFor="file-upload"
              className="text-center flex items-center justify-center flex-col w-full h-full cursor-pointer"
            >
              <UploadIcon className="" />
              <p className="text-brand font-medium text-sm">
                {selectedFile
                  ? selectedFile.name
                  : translate('text-browse-file', { ns: 'quickorder' })}
              </p>
            </label>
            <input
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              type="file"
              accept=".csv"
              value={selectedFile ? undefined : ''}
            />
          </div>
          <Button
            className="text-xs md:text-base  md:w-full !h-[45px] "
            onClick={handleUpload}
            data-testid="quickorder-actions-section-button-create-order"
            loading={isLoading || isOrderLoading}
          >
            {translate('text-upload', { ns: 'cart' })}
          </Button>
        </div>
      </div>
    </>
  )
}

export default CreateImportOrder
